import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { preview } from '@/components/form/preview'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'
import { Button, Stack } from '@mui/material'
import { Chip } from '@workwave-tidal/core/components'

import { selectTerritories } from '@/features/domain/territory'

import { useTexts } from './hooks/useTexts'

interface Props {
  territoryFilter: string[]
}

const DEFAULT_HEIGHT_OF_TWO_ROWS = 56

export function Territories(props: Props) {
  const { territoryFilter } = props
  const territories = useTerritories(territoryFilter)

  const texts = useTexts()

  const [showAllTerritories, setShowAllTerritories] = useState(false)
  const [isTerritoriesOverflowing, setIsTerritoriesOverflowing] = useState(false)

  const territoriesContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Check if the territory container height exceeds default height of 2 rows
    if (territoriesContainerRef.current) {
      setIsTerritoriesOverflowing(
        territoriesContainerRef.current.scrollHeight > DEFAULT_HEIGHT_OF_TWO_ROWS,
      )
    }
  }, [territories.length])

  const noRestriction = territoryFilter[0] === '*'

  if (noRestriction) return null

  return (
    <preview.Field
      label={texts.labels.territories(territories.length)}
      data-trackid="territories"
      testId="territories"
    >
      <Stack
        gap={0.5}
        direction="row"
        flexWrap="wrap"
        ref={territoriesContainerRef}
        sx={{
          maxHeight: showAllTerritories ? 'none' : `${DEFAULT_HEIGHT_OF_TWO_ROWS}px`,
          overflow: 'hidden',
          transition: 'max-height 0.3s ease',
        }}
      >
        {territories.map(territory => (
          <Chip size="small" label={territory.name} key={territory.id} />
        ))}
      </Stack>
      {isTerritoriesOverflowing && (
        <Button
          size="small"
          disableRipple
          sx={{
            textAlign: 'left',
            justifyContent: 'flex-start',
            width: '100%',
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
          }}
          onClick={() => setShowAllTerritories(!showAllTerritories)}
        >
          <Stack
            gap={1}
            direction="row"
            alignItems="center"
            textAlign="center"
            data-testid={`territories-show-${showAllTerritories ? 'less' : 'more'}`}
            data-trackid={`territories-show-${showAllTerritories ? 'less' : 'more'}`}
          >
            {showAllTerritories ? (
              <>
                {texts.showLess} <KeyboardArrowUp color="primary" fontSize="small" />
              </>
            ) : (
              <>
                {texts.showMore} <KeyboardArrowDown color="primary" fontSize="small" />
              </>
            )}
          </Stack>
        </Button>
      )}
    </preview.Field>
  )
}

function useTerritories(territoryFilter: string[]): uui.domain.client.rm.Territory[] {
  const allTerritories = useSelector(selectTerritories)

  return useMemo(() => {
    return territoryFilter[0] === '*'
      ? []
      : territoryFilter.map(territoryId => allTerritories[territoryId]).sort(sortTerritories)
  }, [allTerritories, territoryFilter])
}

function sortTerritories(a: uui.domain.client.rm.Territory, b: uui.domain.client.rm.Territory) {
  return a.name.localeCompare(b.name)
}
